import { GroupMemberInvitationStatus, GroupMemberRole } from '@group/domain';
import { GroupConsoleTitle } from '../common';
import { useGroupMemberInvitations } from './useGroupMemberInvitations';
import { UserIcon } from '@framework/ui/atoms/UserIcon';
import { DeactivateMenuButton } from './DeactivateMenuButton';
import { ReinviteMenuButton } from './ReinviteMenuButton';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { useTranslation } from 'react-i18next';
import { buildDateTimeArgs } from '@framework/i18n/buildDateTimeArgs';
import { FullScreenLoading } from '@framework/ui';

export const GroupConsoleInvitationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { group } = useGroupConsoleOutletContext();
  const invitations = useGroupMemberInvitations(group.id);

  // 取得中(null)と0件(空配列)を区別する。区別せずに「招待が見つかりません」を表示すると、
  // 取得完了までの間だけ誤った表示になってしまう。
  if (!invitations) return <FullScreenLoading />;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <GroupConsoleTitle title={t('group.console.nav.inviteList')} />
      </div>
      <div className="overflow-y-scroll pb-8" style={{ maxHeight: 'calc(100% - 80px)' }}>
        <table className="w-full border-separate">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left">
              <th className="border-y-2 p-2">{t('group.invite.emailSection.label')}</th>
              <th className="border-y-2 p-2">{t('group.members.table.role')}</th>
              <th className="border-y-2 p-2">{t('group.invitations.table.inviter')}</th>
              <th className="border-y-2 p-2">{t('group.invitations.table.invitedAt')}</th>
              <th className="border-y-2 p-2">{t('group.invitations.table.expiresAt')}</th>
              <th className="border-y-2 p-2">{t('group.invitations.table.status')}</th>
              <th className="border-y-2 p-2" />
            </tr>
          </thead>
          <tbody>
            {invitations.length <= 0 ? (
              <tr>
                <td>{t('group.invitations.table.notFound')}</td>
              </tr>
            ) : (
              invitations.map((i) => (
                <tr key={i.id} className="border-b-2">
                  <td className="border-b-2 border-gray-400 py-4 align-middle">{i.mailAddress}</td>
                  <td className="border-b-2 border-gray-400 py-4 align-middle">
                    {t(GroupMemberRole.getLocalStringKey(i.invitation.memberRole))}
                  </td>
                  <td className="border-b-2 border-gray-400 py-4 align-middle">
                    <div className="flex items-center">
                      <div className="mr-1 flex size-10 items-center justify-center">
                        <UserIcon
                          name={i.senderUser.name}
                          id={i.senderUser.userId}
                          imageUrl={i.senderUser.imageUrl}
                          isAnonymous={false}
                          size="large"
                          className="grow-0"
                        />
                      </div>
                      <div className="flex flex-col">
                        {i.senderUser.name}
                        <span className="text-sm text-gray-600">{i.senderUser.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="relative border-b-2 border-gray-400 py-4 align-middle">
                    {t('common.dateTimeFormat', buildDateTimeArgs(i.createdAt))}
                  </td>
                  <td className="relative border-b-2 border-gray-400 py-4 align-middle">
                    {t('common.dateTimeFormat', buildDateTimeArgs(i.expiresAt))}
                  </td>
                  <td className="relative border-b-2 border-gray-400 py-4 align-middle">
                    {t(GroupMemberInvitationStatus.getLocalStringKey(i.status))}
                  </td>
                  <td className="relative border-b-2 border-gray-400 py-4 align-middle">
                    {/* 承諾待ちの時のみ表示 */}
                    {i.isPending() && <DeactivateMenuButton invitation={i} />}
                    {/* 期限切れの時のみ表示 */}
                    {i.status === 'expired' && <ReinviteMenuButton invitations={{ whole: invitations, selected: i }} />}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
